"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { Edit, ShieldAlert, ChevronsUpDown, Check } from "lucide-react";
import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Switch } from "@/app/_module/components/ui/switch";

import { useUserContext } from "@/app/_module/store/contexts/userContext";

import { Button } from "@/app/_module/components/ui/button";
import {
  Dialog,
  DialogTrigger,
  ResponsiveDialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@/app/_module/components/ui/dialog";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/app/_module/components/ui/form";
import { Input } from "@/app/_module/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/_module/components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/app/_module/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/app/_module/components/ui/popover";
import { FilterPop } from "@/app/_module/components/ui/shell-popover";
import { cn } from "@/app/_module/lib/utils";

import { useResourceCategories, useAgencyCarriers } from "../hooks/use-hook";
import type { ResourceType } from "../types/types";

const editResourceSchema = z.object({
  title: z.string().min(1, "Title is required").max(255, "Title is too long"),
  categoryId: z.string().min(1, "Category is required"),
  carrier: z.string().min(1, "Carrier is required"),
  description: z.string().optional(),
  scope: z.enum(["personal", "agency-only", "agency-and-below", "agency-wide"]).optional(),
  isSensitive: z.boolean().optional(),
});

type EditFormData = z.infer<typeof editResourceSchema>;

interface EditResourceModalProps {
  resource: ResourceType;
  onEdit: (resourceId: string, data: EditFormData, close: () => void) => void;
  loading?: boolean;
}

const EditResourceModal = function ({
  resource,
  onEdit,
  loading = false,
}: EditResourceModalProps) {
  const [open, setOpen] = useState(false);
  const [carrierOpen, setCarrierOpen] = useState(false);
  const { data: categories = [], isLoading: categoriesLoading } =
    useResourceCategories();

  // Fetch agency carriers from settings (same as create modal)
  const { data: agencyCarriers = [], isLoading: carriersLoading } = useAgencyCarriers();

  // Get user context to check if user belongs to an agency
  const { user } = useUserContext();
  const userAgencyId = user?.user?.agencyId || user?.currentAgency?.agency?.id;

  // Helper function to get category display value (UUID or friendly name)
  const getCategoryValue = (category: { id: string; name: string }) => {
    // Backend now accepts both UUIDs and friendly names
    // Use friendly name for better UX, fallback to UUID if needed
    return category.name || category.id;
  };

  // Helper function to find category by ID or name for default value
  const findCategoryValue = (categoryId: string) => {
    const category = categories.find(cat => cat.id === categoryId || cat.name === categoryId);
    return category ? getCategoryValue(category) : categoryId;
  };

  const form = useForm<EditFormData>({
    resolver: zodResolver(editResourceSchema),
    defaultValues: {
      title: resource.title,
      categoryId: findCategoryValue(resource.categoryId),
      carrier: resource.carrier,
      description: resource.description,
      scope: resource.scope || "personal",
      isSensitive: resource.isSensitive || false,
    },
    mode: "onChange",
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isValid, isDirty },
    reset,
  } = form;

  useEffect(() => {
    if (open) {
      reset({
        title: resource.title,
        categoryId: findCategoryValue(resource.categoryId),
        carrier: resource.carrier,
        description: resource.description,
        scope: resource.scope || "personal",
        isSensitive: resource.isSensitive || false,
      });
    }
  }, [
    open,
    resource.title,
    resource.categoryId,
    resource.carrier,
    resource.description,
    resource.scope,
    resource.isSensitive,
    reset,
    categories, // Add categories dependency for findCategoryValue
  ]);

  const onSubmit = (data: EditFormData) => {
    onEdit(resource.id, data, () => setOpen(false));
  };

  const handleClose = () => {
    setOpen(false);
    reset({
      title: resource.title,
      categoryId: findCategoryValue(resource.categoryId),
      carrier: resource.carrier,
      description: resource.description,
      scope: resource.scope || "personal",
      isSensitive: resource.isSensitive || false,
    });
  };

  return (
    <Dialog onOpenChange={setOpen} open={open}>
      <DialogTrigger asChild>
        <Button
          className="h-8 px-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
          size="sm"
          variant="ghost"
        >
          <Edit className="h-4 w-4 mr-1" />
          Edit
        </Button>
      </DialogTrigger>

      <ResponsiveDialogContent size="md">
        <DialogHeader>
          <DialogTitle>Edit Resource</DialogTitle>
          <DialogDescription>
            Update the title for "{resource.title}"
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
            <FormField
              control={control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter resource title"
                      {...field}
                      disabled={loading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Category Field */}
            <FormField
              control={control}
              name="categoryId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Category</FormLabel>
                  <FormControl>
                    <Select
                      disabled={categoriesLoading || loading}
                      onValueChange={field.onChange}
                      value={field.value}
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={
                            categoriesLoading ? "Loading..." : "Select category"
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((category) => (
                          <SelectItem key={category.id} value={getCategoryValue(category)}>
                            {category.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Carrier Field */}
            <FormField
              control={control}
              name="carrier"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Insurance Carrier</FormLabel>
                  <FilterPop
                    open={carrierOpen}
                    onOpenChange={setCarrierOpen}
                    modal
                    className="w-[--radix-popover-trigger-width] p-0"
                    trigger={
                      <FormControl>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={carrierOpen}
                          disabled={carriersLoading || loading}
                          className="w-full justify-between font-normal"
                        >
                          {field.value || (carriersLoading ? "Loading carriers..." : "Select carrier")}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </FormControl>
                    }
                  >
                      <Command>
                        <CommandInput placeholder="Search carriers..." />
                        <CommandList className="max-h-[200px]">
                          <CommandEmpty>No carriers found.</CommandEmpty>
                          <CommandGroup>
                            {agencyCarriers.map((carrier) => (
                              <CommandItem
                                key={carrier.name}
                                value={carrier.name}
                                onSelect={() => {
                                  field.onChange(carrier.name);
                                  setCarrierOpen(false);
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    field.value === carrier.name ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                {carrier.name}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                  </FilterPop>
                  <FormMessage />
                  {agencyCarriers.length === 0 && !carriersLoading && (
                    <p className="text-sm text-yellow-600 mt-1">
                      Please configure carriers in Settings.
                    </p>
                  )}
                </FormItem>
              )}
            />

            {/* Description Field */}
            <FormField
              control={control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description (Optional)</FormLabel>
                  <FormControl>
                    <textarea
                      className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      placeholder="Enter resource description"
                      {...field}
                      disabled={loading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Visibility Scope */}
            <FormField
              control={control}
              name="scope"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Visibility</FormLabel>
                  <FormControl>
                    <Select
                      disabled={loading}
                      onValueChange={field.onChange}
                      value={field.value || 'personal'}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Who can see this resource?" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="personal">
                          <span className="font-medium">Personal</span>
                          <span className="text-muted-foreground ml-2">- Only visible to you</span>
                        </SelectItem>
                        {userAgencyId && (
                          <>
                            <SelectItem value="agency-only">
                              <span className="font-medium">My Agency</span>
                              <span className="text-muted-foreground ml-2">- Direct team only</span>
                            </SelectItem>
                            {['owner', 'admin'].includes(((user as any)?.currentAgency?.role || '').toLowerCase()) && (
                              <SelectItem value="agency-and-below">
                                <span className="font-medium">My Agency + Subs</span>
                                <span className="text-muted-foreground ml-2">- Team & sub-agencies</span>
                              </SelectItem>
                            )}
                            {['owner', 'admin'].includes(((user as any)?.currentAgency?.role || '').toLowerCase()) && !(user as any)?.currentAgency?.agency?.parentAgencyId && (
                              <SelectItem value="agency-wide">
                                <span className="font-medium">Agency Wide</span>
                                <span className="text-muted-foreground ml-2">- Entire organization</span>
                              </SelectItem>
                            )}
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  {!userAgencyId && (
                    <p className="text-xs text-muted-foreground">
                      Join an agency to share resources with your team
                    </p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Sensitive Info Toggle */}
            <FormField
              control={control}
              name="isSensitive"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4 bg-amber-50/50 border-amber-200">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base flex items-center gap-2">
                      <ShieldAlert className="h-4 w-4 text-amber-600" />
                      Contains Sensitive Information
                    </FormLabel>
                    <p className="text-sm text-muted-foreground">
                      Mark this resource if it contains SSN, DOB, financial data, or other PII.
                      {resource.autoDetectedSensitive && (
                        <span className="block text-amber-600 mt-1">
                          Sensitivity was auto-detected during upload.
                        </span>
                      )}
                    </p>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={loading}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter className="flex justify-between">
              <DialogClose asChild>
                <Button
                  disabled={loading}
                  onClick={handleClose}
                  type="button"
                  variant="outline"
                >
                  Cancel
                </Button>
              </DialogClose>
              <Button disabled={!isValid || !isDirty || loading} type="submit">
                {loading ? (
                  <React.Fragment>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Updating...
                  </React.Fragment>
                ) : (
                  "Update Resource"
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </ResponsiveDialogContent>
    </Dialog>
  );
};

export default EditResourceModal;
